'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useClipboard, useFocusWithin, useKeyboard } from 'react-aria';
import type { TextDropItem } from 'react-aria';
import { ChipBase, type ChipBaseProps } from './ChipBase';
import {
    CONTENT_EDITABLE_PLAINTEXT_ONLY,
    insertTextAtSelection,
    normalizeToSingleLinePlainText,
    selectAll,
    trimTextToMaxLengthAtSelection,
} from '../../../utils/contentEditable';
import { cx } from '../../../utils/cx';

const SEMANTIC_NAME_EDITABLE = 'RomaChipEditable';

// React's HTML attribute typing in this codebase does not yet include
// contentEditable="plaintext-only", so we cast to the existing type.
const PLAINTEXT_ONLY_CONTENT_EDITABLE =
    CONTENT_EDITABLE_PLAINTEXT_ONLY as unknown as React.HTMLAttributes<HTMLSpanElement>['contentEditable'];

export type ChipEditingChangeReason = 'enter' | 'blur' | 'escape' | 'double-click' | 'keyboard';

export type ChipEditableProps = Omit<
    ChipBaseProps,
    // Managed internally by ChipEditable — consumers should not set these directly.
    'isSelected' | 'children' | 'tabIndex' | 'onDoubleClick' | 'onKeyDown' | 'onFocus' | 'onBlur'
> & {
    /** The current committed label text. */
    value: string;
    /** Uncontrolled: whether to start in edit mode. */
    defaultEditing?: boolean;
    /** Controlled: whether the tag is currently in edit mode. */
    isEditing?: boolean;
    /** Called when the component wants to enter or exit edit mode with an optional reason for the change. */
    onEditingChange?: (isEditing: boolean, details?: { reason: ChipEditingChangeReason }) => void;
    /** Called when backspace/delete is pressed while the edit value is already empty. */
    onEmptyBackspace?: () => void;
    /** Called with the trimmed new text when the user commits a non-empty edit. */
    onCommit?: (value: string) => void;
    /** Called when the user cancels an edit via Escape. */
    onCancel?: () => void;
    /** Accessible label for the contentEditable span. Defaults to "Edit chip name". */
    editAriaLabel?: string;
    /** Maximum character length enforced on keydown. */
    maxLength?: number;
};

type EditableTextSlotProps = {
    textRef: React.RefObject<HTMLSpanElement>;
    isDisabled?: boolean;
    editAriaLabel: string;
    maxLength?: number;
    initialValue: string;
    onEmptyBackspace?: () => void;
    onCommit: () => void;
    onCancel: () => void;
};

const EditableTextSlot: React.FC<EditableTextSlotProps> = ({
    textRef,
    isDisabled,
    editAriaLabel,
    maxLength,
    initialValue,
    onEmptyBackspace,
    onCommit,
    onCancel,
}) => {
    const getCurrentEditableValue = () =>
        normalizeToSingleLinePlainText(textRef.current?.textContent ?? textRef.current?.innerText ?? '');

    // Populate and select all text on mount before paint so controlled entry does not
    // show an empty frame while React swaps display mode for the editable slot.
    useLayoutEffect(() => {
        if (!textRef.current) return;
        textRef.current.innerText = initialValue;
        selectAll(textRef.current);
    }, [initialValue, textRef]);

    // Keyboard handler for the contentEditable span in edit mode.
    // Enter commits (blocking the default newline), Escape cancels.
    const { keyboardProps } = useKeyboard({
        onKeyDown: (event) => {
            if (event.key === 'Backspace' || event.key === 'Delete') {
                event.stopPropagation();

                const current = getCurrentEditableValue();
                if (!current.length && onEmptyBackspace) {
                    event.preventDefault();
                    onEmptyBackspace();
                }

                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                onCommit();
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                onCancel();
                return;
            }

            if (maxLength !== undefined) {
                const current = getCurrentEditableValue();
                // Account for selected text that would be replaced — allow typing when the
                // net character count after replacement would not exceed maxLength.
                const selectedLength = window.getSelection()?.toString().length ?? 0;
                const isCharKey = event.key.length === 1 && !event.metaKey && !event.ctrlKey;
                if (isCharKey && current.length - selectedLength >= maxLength) event.preventDefault();
            }
        },
    });

    // Plain-text, single-line paste handler for the contentEditable span.
    // useClipboard covers both ⌘V and browser Edit → Paste menu commands.
    const { clipboardProps } = useClipboard({
        isDisabled: isDisabled,
        async onPaste(items) {
            const texts = await Promise.all(
                items
                    .filter((item): item is TextDropItem => item.kind === 'text' && item.types.has('text/plain'))
                    .map((item) => item.getText('text/plain')),
            );
            const plain = texts.join('');
            const singleLine = normalizeToSingleLinePlainText(plain);
            const trimmedToMaxLength = trimTextToMaxLengthAtSelection({
                text: singleLine,
                root: textRef.current,
                maxLength,
            });
            insertTextAtSelection({ root: textRef.current, text: trimmedToMaxLength });
        },
    });

    // Edit-mode text slot.
    // - contentEditable="plaintext-only": browser-level rich text suppression.
    // - `overflow-x-auto scrollbar-hide`: when constrained, the editable host scrolls
    //   horizontally without showing a scrollbar, matching standard single-line inputs.
    // - `whitespace-nowrap`: keeps the tag single-line.
    // - `outline-none`: browser outline suppressed; focus ring lives on the chip.
    // - `inline-block min-w-[1ch] max-w-full`: makes overflow apply to the span while
    //   preventing it from collapsing to zero width while editing.
    return (
        <span
            ref={textRef}
            contentEditable={PLAINTEXT_ONLY_CONTENT_EDITABLE}
            suppressContentEditableWarning
            role="textbox"
            aria-label={editAriaLabel}
            className="scrollbar-hide inline-block min-w-[1ch] max-w-full overflow-x-auto whitespace-nowrap outline-none"
            {...keyboardProps}
            {...clipboardProps}
        >
            {initialValue}
        </span>
    );
};

export const ChipEditable = React.forwardRef<HTMLSpanElement, ChipEditableProps>(
    (
        {
            value,
            defaultEditing = false,
            isEditing: controlledIsEditing,
            onEditingChange,
            onEmptyBackspace,
            onCommit,
            onCancel,
            editAriaLabel = 'Edit name',
            maxLength,
            isDisabled,
            className,
            ...chipBaseProps
        },
        forwardedRef,
    ) => {
        const [internalIsEditing, setInternalIsEditing] = useState(defaultEditing);
        const isControlled = controlledIsEditing !== undefined;
        const isEditing = isControlled ? controlledIsEditing : internalIsEditing;

        const textSpanRef = useRef<HTMLSpanElement>(null);
        const chipRef = useRef<HTMLSpanElement>(null);

        React.useImperativeHandle(forwardedRef, () => chipRef.current as HTMLSpanElement);
        const lastCommittedValueRef = useRef(value);

        // Keep lastCommittedValueRef in sync when value changes externally while not editing.
        useEffect(() => {
            if (!isEditing) lastCommittedValueRef.current = value;
        }, [value, isEditing]);

        const enterEditMode = () => {
            if (isDisabled) return;
            if (!isControlled) setInternalIsEditing(true);
            onEditingChange?.(true);
        };

        const exitEditMode = (reason: ChipEditingChangeReason) => {
            if (!isControlled) setInternalIsEditing(false);
            onEditingChange?.(false, { reason });
        };

        const commit = (reason: 'enter' | 'blur') => {
            const trimmed = (textSpanRef.current?.innerText ?? '').trim();
            if (trimmed) {
                lastCommittedValueRef.current = trimmed;
                onCommit?.(trimmed);
            } else {
                // Revert rather than committing an empty value.
                if (textSpanRef.current) textSpanRef.current.innerText = lastCommittedValueRef.current;
            }
            exitEditMode(reason);
        };

        const cancel = () => {
            if (textSpanRef.current) textSpanRef.current.innerText = lastCommittedValueRef.current;
            onCancel?.();
            exitEditMode('escape');
        };

        const focusChip = () => {
            chipRef.current?.focus();
        };

        const handleDoubleClick = () => {
            if (!isEditing) enterEditMode();
        };

        const handleCommitFromEditable = () => {
            commit('enter');
            focusChip();
        };

        const handleCancelFromEditable = () => {
            cancel();
            focusChip();
        };

        // ─── React Aria hooks ────────────────────────────────────────────────────

        // Keyboard handler for the chip in display mode.
        // useKeyboard stops propagation by default — Enter/F2 won't bubble to parent containers.
        const { keyboardProps: chipKeyboardProps } = useKeyboard({
            onKeyDown: (event) => {
                if (isEditing) return;

                if (event.key === 'Enter' || event.key === 'F2') {
                    event.preventDefault();
                    enterEditMode();
                    return;
                }

                event.continuePropagation();
            },
        });

        // Blur handler placed on the chip container rather than the contentEditable span.
        // useFocusWithin fires onBlurWithin only when focus leaves the chip and all its
        // descendants, so:
        //   • Enter/Escape → chipRef.focus() keeps focus within the subtree → no spurious commit.
        //   • Clicking the remove button keeps focus within the subtree → no spurious commit.
        //   • Clicking outside → focus leaves the subtree → commit fires once, cleanly.
        const { focusWithinProps } = useFocusWithin({
            isDisabled: !isEditing || isDisabled,
            onBlurWithin: () => commit('blur'),
        });

        // ─── Render ──────────────────────────────────────────────────────────────

        return (
            <ChipBase
                ref={chipRef}
                {...chipBaseProps}
                isDisabled={isDisabled}
                tabIndex={isEditing ? -1 : 0}
                onDoubleClick={handleDoubleClick}
                onKeyDown={chipKeyboardProps.onKeyDown}
                onFocus={focusWithinProps.onFocus}
                onBlur={focusWithinProps.onBlur}
                className={cx(
                    SEMANTIC_NAME_EDITABLE,
                    // Custom keyboard focus ring — suppress browser default, show design-system
                    // outline on focus-visible. Uses outline (not ring) so it never conflicts
                    // with the edit-mode ring-2 below.
                    'min-w-0 max-w-full focus:outline-hidden focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring',
                    isEditing && 'ring-2 ring-primary_focused',
                    // Prevent accidental text selection in display mode. Omitted in edit mode
                    // so the contentEditable span stays selectable.
                    !isEditing && 'select-none',
                    !isDisabled && 'cursor-pointer',
                    className,
                )}
            >
                {isEditing ? (
                    <EditableTextSlot
                        textRef={textSpanRef}
                        isDisabled={isDisabled}
                        editAriaLabel={editAriaLabel}
                        maxLength={maxLength}
                        initialValue={lastCommittedValueRef.current}
                        onEmptyBackspace={onEmptyBackspace}
                        onCommit={handleCommitFromEditable}
                        onCancel={handleCancelFromEditable}
                    />
                ) : (
                    <span className="truncate">{value}</span>
                )}
            </ChipBase>
        );
    },
);

ChipEditable.displayName = SEMANTIC_NAME_EDITABLE;
